import React from 'react';
import PropTypes from 'prop-types';
import { View, TouchableWithoutFeedback } from 'react-native';

import Janitor from '../../../../modules/Janitor';
import ssPropTypes from '../../../../modules/ssPropTypes';

import Select from '../../../forms/Select';
import ImageEditor, { ImageEditorContainerProps } from '../../ImageEditor';
import FormInput from '../../../ui/FormInput';
import BodyText from '../../../ui/BodyText';

import styles from './ImageEditorForm.module.scss';
import withHook from '../../../withHook';
import { useParams } from '../../../../hooks';
import { ProClientSelectContainer } from '../../../provider/clients/ProClientSelect/ProClientSelectContainer';
import { checkFromMarketingTips } from '../../../provider/MarketingHub/insights';

export const ImageEditorFormProps = {
  /** Pass ImageEditor props through  */
  ...ImageEditorContainerProps,
  /** The URL to the image */
  image: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(File),
  ]),

  /** The image caption. */
  caption: PropTypes.string,

  /** True to support advanced fields, otherwise false */
  supportsAdvancedFields: PropTypes.bool,

  /** True to support setting a caption, otherwise false. Default is true. */
  supportsCaption: PropTypes.bool,

  /** The service attached to this image */
  serviceId: PropTypes.number,

  /** The client ID attached to this image. */
  clientId: PropTypes.number,

  /** Handler used by AvatarEditor to assign its ref */
  onEditorMounted: PropTypes.func,

  /** Some of the data of the image has changed (does not include crop/rotate/zoom)
   * IMPORTANT NOTE: The angular version of this component (in ImageEditorForm.ng) provides an
   * additional "index" property to this function. See `ImageEditorForm.ng` for more information.
   */
  onChange: PropTypes.func,

  /** Services available for association with an image */
  serviceGroups: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      services: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          id: ssPropTypes.numberLike,
        }),
      ),
    }),
  ).isRequired,

  /** Given an ID, yields client information for the client with that ID */
  loadClient: PropTypes.func.isRequired,

  routeParams: PropTypes.object,
};

/**
 * Renders a component that allows editing of image cropping, zoom, and rotation, as well as
 * metadata specific to a pro, such as client and service.
 */
export class ImageEditorFormBase extends React.PureComponent {
  static propTypes = ImageEditorFormProps;

  static defaultProps = {
    image: null,
    caption: '',
    serviceId: null,
    clientId: null,
    onEditorMounted: null,
    onChange: null,
    supportsAdvancedFields: false,
    supportsCaption: true,
    routeParams: {},
  };

  state = {
    client: null,
    selectedServiceId: null,
    originalCaption: this.props.caption,
    caption: this.props.caption || '',
    isClientSelectModalOpen: false,
  };

  janitor = new Janitor();

  imageChanges = {};

  static getDerivedStateFromProps(props, state) {
    const newState = {};

    if (state.selectedServiceId === null) {
      newState.selectedServiceId = props.serviceId;
    }
    if (props.caption !== state.originalCaption) {
      newState.caption = props.caption;
    }

    return newState;
  }

  componentDidMount() {
    this.loadClient();
    this.getStateFromRouteParams();
  }

  componentDidUpdate(prevProps) {
    const { client } = this.state;
    if (!client && prevProps.clientId !== this.props.clientId) {
      this.loadClient();
    }
  }

  componentWillUnmount() {
    this.janitor.cleanup();
  }

  getStateFromRouteParams() {
    const { routeParams } = this.props;
    const newStateFromRouteParams = {};
    const { fromMarketingTips, serviceId } = checkFromMarketingTips(routeParams);
    newStateFromRouteParams.fromMarketingTip = fromMarketingTips;

    if (serviceId) {
      newStateFromRouteParams.selectedServiceId = this.state.selectedServiceId
        || serviceId;
      newStateFromRouteParams.marketingTipServiceId = serviceId;
    }

    this.setState({ ...newStateFromRouteParams }, () => this.fireChangeHandler());
  }

  /**
   * Fire the change handler with all the image data.
   */
  fireChangeHandler() {
    const { id, onChange } = this.props;
    const {
      client, selectedServiceId, caption, fromMarketingTip, marketingTipServiceId,
    } = this.state;

    if (typeof onChange === 'function') {
      onChange({
        id,
        client,
        caption,
        service: selectedServiceId ? Number(selectedServiceId) : null,
        crop: this.imageChanges,
        marketingTipCompleted: fromMarketingTip
          && (
            (marketingTipServiceId && marketingTipServiceId === selectedServiceId)
            || (!marketingTipServiceId && selectedServiceId)
          ),
      });
    }
  }

  /**
   * Handle changes to the image
   */
  handleImageChange = data => {
    this.imageChanges = data;
    this.fireChangeHandler();
  };

  /**
   * Load services for the provider.
   */
  transformServices() {
    const { serviceGroups = [] } = this.props;
    let results = {};

    serviceGroups.forEach(group => {
      if (!group.services.length) {
        return;
      }

      // Organize into object of <SERVICE ID>: <SERVICE NAME>
      results[group.name || '---'] = group.services.reduce((acc, service) => {
        acc[service.id] = service.name;
        return acc;
      }, {});
    });

    if (Object.keys(results).length === 1) {
      results = Object.values(results)[0];
    }

    return results;
  }

  /**
   * Load the client object from the clientId prop.
   */
  loadClient() {
    const { clientId, loadClient } = this.props;

    if (typeof loadClient === 'function' && clientId) {
      loadClient(clientId).then(client => {
        this.setState({ client: { ...client } });
      });
    } else {
      this.setState({ client: null });
    }
  }

  /**
   * Select a client for this photo
   */
  handleSelectClientClick = () => {
    this.setState({ isClientSelectModalOpen: true });
  };

  handleClientSelectClose = () => {
    this.setState({ isClientSelectModalOpen: false });
  };

  /**
   * Select a client for this photo
   */
  handleClientSelected = client => {
    this.setState({ client }, () => this.fireChangeHandler());
  };

  /**
   * Service was changed
   */
  handleServiceChange = value => {
    this.setState({ selectedServiceId: value }, () => this.fireChangeHandler());
  };

  /**
   * Caption text changed
   */
  handleCaptionChange = evt => {
    this.setState(
      {
        caption: evt.target.value || '',
      },
      () => this.fireChangeHandler(),
    );
  };

  render() {
    const {
      image, supportsAdvancedFields, supportsCaption,
    } = this.props;
    const {
      selectedServiceId, caption, isClientSelectModalOpen,
    } = this.state;

    const { client } = this.state;

    const editorProps = {};
    Object.keys(ImageEditorContainerProps || {}).forEach(key => {
      editorProps[key] = this.props[key];
    });

    const serviceGroups = this.transformServices();

    return (
      <View styleName="pro-image-editor" testID="pro-image-editor">
        <View>
          <ImageEditor {...editorProps} image={image} onChange={this.handleImageChange} />
        </View>
        <View styleName="image-fields">
          {supportsAdvancedFields ? (
            <React.Fragment>
              <View styleName="field-row">
                <BodyText styleName="field-row-label">Service:</BodyText>
                <Select
                  styleName={selectedServiceId ? 'field-value-filled' : 'field-value'}
                  hasEmptyItem
                  selected={selectedServiceId}
                  options={serviceGroups}
                  placeholder={Object.keys(serviceGroups).length ? 'Select Service' : 'Loading...'}
                  onChange={this.handleServiceChange}
                  testID="image-editor-service-select"
                />
              </View>
              <View>
                <TouchableWithoutFeedback
                  onPress={this.handleSelectClientClick}
                  testID="image-editor-client-select"
                >
                  <View styleName="field-row">
                    <BodyText styleName="field-row-label">Client:</BodyText>
                    <BodyText
                      styleName={client ? 'field-value-filled' : 'field-value'}
                      testID="image-editor-client-select-selection-text"
                    >
                      {client ? client.name : 'Select Client'}
                    </BodyText>
                  </View>
                </TouchableWithoutFeedback>
              </View>
            </React.Fragment>
          ) : null}
          {supportsCaption && (
            <View>
              <FormInput
                inputClassName={styles.captionInput}
                styleName="caption-text"
                type="textarea"
                onChange={this.handleCaptionChange}
                placeholder="Add a short description"
                value={caption}
                testID="image-editor-caption-text"
              />
            </View>
          )}
        </View>
        {isClientSelectModalOpen ? (
          <ProClientSelectContainer
            onClientSelected={this.handleClientSelected}
            onClose={this.handleClientSelectClose}
          />
        ) : null}
      </View>
    );
  }
}

export default withHook(ImageEditorFormBase, 'routeParams', useParams);
